import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    FormHelperText,
    Grid,
    TextField,
    Typography,
} from '@mui/material';
import { getDataFull, postData } from '../../services/ajax';
import OsmLocationMap from '../map/OsmLocationMap';
import { normalizeStreetName } from '../map/nominatimGeocode';

const GEOCODE_DEBOUNCE_MS = 700;
const SIN_CALLE_LABEL = 'Sin calle';
const SIN_CALLE_OPTION = { id: 'sin_calle', nombre: SIN_CALLE_LABEL, sinCalle: true };

const emptyValue = () => ({
    geo_provincia_id: '',
    geo_departamento_id: '',
    geo_localidad_id: '',
    geo_calle_id: null,
    calle_texto: '',
    altura: '',
    piso: '',
    depto: '',
    referencia: '',
    codigo_postal: '',
    latitud: null,
    longitud: null,
    origen_coords: null,
    provincia_nombre: '',
    departamento_nombre: '',
    localidad_nombre: '',
});

const autocompletePopperSx = {
    slotProps: {
        popper: {
            sx: { zIndex: 1300 },
        },
    },
};

const mobileInputSx = {
    '& .MuiInputBase-input': {
        fontSize: { xs: '16px', sm: '0.875rem' },
    },
};

export function isSinCalle(value) {
    if (!value) return false;
    if (value.geo_calle_id) return false;
    const t = String(value.calle_texto || '').trim().toLowerCase();
    return t === 'sin calle';
}

function optionById(list, id, fallbackNombre) {
    if (id === '' || id == null) return null;
    const found = list.find((item) => Number(item.id) === Number(id));
    if (found) return found;
    if (fallbackNombre) return { id: Number(id), nombre: fallbackNombre };
    return null;
}

/**
 * Formulario de dirección Georef controlado.
 */
export default function GeoDireccionForm({
    token,
    value,
    onChange,
    required = false,
    disabled = false,
    title = 'Domicilio',
    allowClear = false,
}) {
    const [provincias, setProvincias] = useState([]);
    const [departamentos, setDepartamentos] = useState([]);
    const [localidades, setLocalidades] = useState([]);
    const [callesOptions, setCallesOptions] = useState([]);
    const [geoConfig, setGeoConfig] = useState({ provincia_codigo_georef: '22', calles_disponibles: true });
    const [calleInput, setCalleInput] = useState('');
    const [geoError, setGeoError] = useState('');
    const [geocoding, setGeocoding] = useState(false);
    const [loadingDepartamentos, setLoadingDepartamentos] = useState(false);
    const [loadingLocalidades, setLoadingLocalidades] = useState(false);

    const geocodeSeqRef = useRef(0);
    const deptoSeqRef = useRef(0);
    const locSeqRef = useRef(0);
    const callesSeqRef = useRef(0);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const stateRef = useRef(value || emptyValue());

    const state = value || emptyValue();
    stateRef.current = state;
    const sinCalle = isSinCalle(state);

    const emit = useCallback((patch) => {
        const next = { ...emptyValue(), ...stateRef.current, ...patch };
        onChangeRef.current?.(next);
    }, []);

    useEffect(() => {
        if (!token) return;
        (async () => {
            const [cfg, prov] = await Promise.all([
                getDataFull('geo/config', token),
                getDataFull('geo/provincias?per_page=100', token),
            ]);
            if (cfg.error === '' && cfg.data) setGeoConfig(cfg.data);
            if (prov.error === '' && prov.data) {
                setProvincias(prov.data);
                if (!state.geo_provincia_id && cfg.data?.provincia_codigo_georef) {
                    const chaco = prov.data.find((p) => p.codigo_georef === cfg.data.provincia_codigo_georef);
                    if (chaco) emit({ geo_provincia_id: chaco.id, provincia_nombre: chaco.nombre || '' });
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        if (!token || !state.geo_provincia_id) {
            setDepartamentos([]);
            setLoadingDepartamentos(false);
            return undefined;
        }
        const seq = ++deptoSeqRef.current;
        setDepartamentos([]);
        setLoadingDepartamentos(true);
        (async () => {
            const res = await getDataFull(
                `geo/departamentos?per_page=500&geo_provincia_id=${state.geo_provincia_id}`,
                token
            );
            if (seq !== deptoSeqRef.current) return;
            setDepartamentos(res.error === '' && res.data ? res.data : []);
            setLoadingDepartamentos(false);
        })();
        return () => {
            deptoSeqRef.current += 1;
        };
    }, [token, state.geo_provincia_id]);

    useEffect(() => {
        if (!token || !state.geo_departamento_id) {
            setLocalidades([]);
            setLoadingLocalidades(false);
            return undefined;
        }
        const seq = ++locSeqRef.current;
        setLocalidades([]);
        setLoadingLocalidades(true);
        (async () => {
            const res = await getDataFull(
                `geo/localidades?per_page=500&geo_departamento_id=${state.geo_departamento_id}`,
                token
            );
            if (seq !== locSeqRef.current) return;
            setLocalidades(res.error === '' && res.data ? res.data : []);
            setLoadingLocalidades(false);
        })();
        return () => {
            locSeqRef.current += 1;
        };
    }, [token, state.geo_departamento_id]);

    const selectedProvincia = useMemo(
        () => optionById(provincias, state.geo_provincia_id, state.provincia_nombre),
        [provincias, state.geo_provincia_id, state.provincia_nombre]
    );
    const selectedDepartamento = useMemo(
        () => optionById(departamentos, state.geo_departamento_id, state.departamento_nombre),
        [departamentos, state.geo_departamento_id, state.departamento_nombre]
    );
    const selectedLocalidad = useMemo(
        () => optionById(localidades, state.geo_localidad_id, state.localidad_nombre),
        [localidades, state.geo_localidad_id, state.localidad_nombre]
    );

    const callesDisponibles = Boolean(
        geoConfig.calles_disponibles
        && selectedProvincia
        && selectedProvincia.codigo_georef === geoConfig.provincia_codigo_georef
    );

    useEffect(() => {
        if (!token || !callesDisponibles || !state.geo_localidad_id || sinCalle) {
            setCallesOptions([]);
            return undefined;
        }
        const q = calleInput.trim();
        if (q.length < 2 || q.toLowerCase() === 'sin calle') {
            setCallesOptions([]);
            return undefined;
        }
        const seq = ++callesSeqRef.current;
        const t = setTimeout(async () => {
            const res = await getDataFull(
                `geo/calles?per_page=50&geo_localidad_id=${state.geo_localidad_id}&search_text=${encodeURIComponent(q)}`,
                token
            );
            if (seq !== callesSeqRef.current) return;
            setCallesOptions(res.error === '' && res.data ? res.data : []);
        }, 300);
        return () => {
            clearTimeout(t);
            callesSeqRef.current += 1;
        };
    }, [token, callesDisponibles, state.geo_localidad_id, calleInput, sinCalle]);

    useEffect(() => {
        setCalleInput(state.calle_texto || '');
    }, [state.calle_texto]);

    const handleLocalidadChange = (loc) => {
        const localidadId = loc?.id ?? '';
        const hasCentroide = loc?.centroide_lat != null && loc?.centroide_lon != null;
        setGeoError('');
        emit({
            geo_localidad_id: localidadId,
            localidad_nombre: loc?.nombre || '',
            geo_calle_id: null,
            calle_texto: '',
            altura: '',
            latitud: hasCentroide ? Number(loc.centroide_lat) : null,
            longitud: hasCentroide ? Number(loc.centroide_lon) : null,
            origen_coords: hasCentroide ? 'centroide' : null,
        });
        setCalleInput('');
    };

    const applySinCalle = () => {
        setGeoError('');
        setCalleInput(SIN_CALLE_LABEL);
        const loc = selectedLocalidad;
        const hasCentroide = loc?.centroide_lat != null && loc?.centroide_lon != null;
        emit({
            geo_calle_id: null,
            calle_texto: SIN_CALLE_LABEL,
            altura: '',
            latitud: hasCentroide
                ? Number(loc.centroide_lat)
                : state.latitud,
            longitud: hasCentroide
                ? Number(loc.centroide_lon)
                : state.longitud,
            origen_coords: hasCentroide ? 'centroide' : (state.origen_coords === 'mapa' ? 'mapa' : null),
        });
    };

    const addressKey = useMemo(() => {
        if (sinCalle) return '';
        const calle = (state.calle_texto || '').trim();
        const altura = (state.altura || '').trim();
        const localidad = selectedLocalidad?.nombre || state.localidad_nombre || '';
        if (!calle || !altura || !localidad) return '';
        return [
            calle,
            altura,
            localidad,
            selectedDepartamento?.nombre || state.departamento_nombre || '',
            selectedProvincia?.nombre || state.provincia_nombre || '',
            state.geo_calle_id || '',
        ].join('|');
    }, [
        sinCalle,
        state.calle_texto,
        state.altura,
        state.localidad_nombre,
        state.departamento_nombre,
        state.provincia_nombre,
        state.geo_calle_id,
        selectedLocalidad,
        selectedDepartamento,
        selectedProvincia,
    ]);

    useEffect(() => {
        if (disabled || !token || !addressKey) {
            return undefined;
        }

        const seq = ++geocodeSeqRef.current;
        setGeocoding(true);
        setGeoError('');

        const timer = setTimeout(async () => {
            const loc = selectedLocalidad;
            const centroideLat = loc?.centroide_lat != null ? Number(loc.centroide_lat) : null;
            const centroideLon = loc?.centroide_lon != null ? Number(loc.centroide_lon) : null;
            const calle = normalizeStreetName((state.calle_texto || '').trim());
            const altura = (state.altura || '').trim();

            const payload = {
                calle,
                altura,
                localidad: loc?.nombre || state.localidad_nombre || '',
                departamento: selectedDepartamento?.nombre || state.departamento_nombre || '',
                provincia: selectedProvincia?.nombre || state.provincia_nombre || '',
                codigo_provincia: selectedProvincia?.codigo_georef || geoConfig.provincia_codigo_georef || '22',
            };
            if (state.geo_calle_id) payload.geo_calle_id = Number(state.geo_calle_id);
            if (centroideLat != null) payload.centroide_lat = centroideLat;
            if (centroideLon != null) payload.centroide_lon = centroideLon;

            const res = await postData('geo/geocodificar', payload, token);
            if (seq !== geocodeSeqRef.current) return;

            if (res.error || res.data?.latitud == null || res.data?.longitud == null) {
                setGeoError(
                    (typeof res.error === 'string' && res.error.trim())
                    || 'No se pudo ubicar automáticamente. Corresponde marcar el punto en el mapa.'
                );
                if (centroideLat != null && centroideLon != null) {
                    emit({
                        latitud: centroideLat,
                        longitud: centroideLon,
                        origen_coords: 'centroide',
                    });
                }
                setGeocoding(false);
                return;
            }

            setGeoError('');
            const fuente = res.data.fuente === 'georef' ? 'georef' : 'nominatim';
            emit({
                latitud: Number(res.data.latitud),
                longitud: Number(res.data.longitud),
                origen_coords: fuente,
            });
            setGeocoding(false);
        }, GEOCODE_DEBOUNCE_MS);

        return () => {
            clearTimeout(timer);
            if (seq === geocodeSeqRef.current) {
                geocodeSeqRef.current += 1;
                setGeocoding(false);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressKey, token, disabled]);

    const mapZoom = (!sinCalle && (state.calle_texto || '').trim() && (state.altura || '').trim()) ? 17 : 15;
    const missingCoords = Boolean(state.geo_localidad_id) && (state.latitud == null || state.longitud == null);
    const coordsLabel = state.latitud != null && state.longitud != null
        ? `${Number(state.latitud).toFixed(6)}, ${Number(state.longitud).toFixed(6)} (${state.origen_coords || '—'})`
        : 'Sin coordenadas';

    const locationStatus = useMemo(() => {
        if (!state.geo_localidad_id) return 'idle';
        if (geocoding) return 'loading';
        if (state.origen_coords === 'mapa') return 'manual_ok';
        if (sinCalle || state.origen_coords === 'centroide' || geoError) return 'manual_required';
        if (state.origen_coords === 'georef' || state.origen_coords === 'nominatim') return 'ok';
        if (state.origen_coords === 'gps') return 'ok';
        return 'idle';
    }, [state.geo_localidad_id, state.origen_coords, geocoding, sinCalle, geoError]);

    const calleOptions = useMemo(
        () => [SIN_CALLE_OPTION, ...(callesDisponibles ? callesOptions : [])],
        [callesDisponibles, callesOptions]
    );

    const calleValue = useMemo(() => {
        if (sinCalle) return SIN_CALLE_OPTION;
        if (state.geo_calle_id) {
            return optionById(callesOptions, state.geo_calle_id, state.calle_texto) || {
                id: state.geo_calle_id,
                nombre: state.calle_texto || '',
            };
        }
        return null;
    }, [sinCalle, state.geo_calle_id, state.calle_texto, callesOptions]);

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: title ? 1 : 0 }}>
                {title ? (
                    <Typography variant="subtitle1" fontWeight={600}>
                        {title}{required ? ' *' : ''}
                    </Typography>
                ) : <span />}
                {allowClear && (
                    <Button size="small" disabled={disabled} onClick={() => onChange?.(null)}>
                        Quitar domicilio
                    </Button>
                )}
            </Box>
            {title ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    La ubicación se usa en la autoasignación. Con “Sin calle”, corresponde marcar el punto en el mapa.
                </Typography>
            ) : null}

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Autocomplete
                        size="small"
                        disabled={disabled}
                        options={provincias}
                        value={selectedProvincia}
                        getOptionLabel={(o) => o?.nombre || ''}
                        isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                        disableClearable={required}
                        onChange={(_, opt) => {
                            setGeoError('');
                            emit({
                                geo_provincia_id: opt?.id ?? '',
                                provincia_nombre: opt?.nombre || '',
                                geo_departamento_id: '',
                                departamento_nombre: '',
                                geo_localidad_id: '',
                                localidad_nombre: '',
                                geo_calle_id: null,
                                calle_texto: '',
                                altura: '',
                                latitud: null,
                                longitud: null,
                                origen_coords: null,
                            });
                            setCalleInput('');
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Provincia"
                                required={required}
                                sx={mobileInputSx}
                            />
                        )}
                        {...autocompletePopperSx}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Autocomplete
                        size="small"
                        disabled={disabled || !state.geo_provincia_id || loadingDepartamentos}
                        options={departamentos}
                        value={selectedDepartamento}
                        getOptionLabel={(o) => o?.nombre || ''}
                        isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                        disableClearable={required}
                        loading={loadingDepartamentos}
                        onChange={(_, opt) => {
                            setGeoError('');
                            emit({
                                geo_departamento_id: opt?.id ?? '',
                                departamento_nombre: opt?.nombre || '',
                                geo_localidad_id: '',
                                localidad_nombre: '',
                                geo_calle_id: null,
                                calle_texto: '',
                                altura: '',
                                latitud: null,
                                longitud: null,
                                origen_coords: null,
                            });
                            setCalleInput('');
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Departamento"
                                required={required}
                                sx={mobileInputSx}
                            />
                        )}
                        {...autocompletePopperSx}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <Autocomplete
                        size="small"
                        disabled={disabled || !state.geo_departamento_id || loadingLocalidades}
                        options={localidades}
                        value={selectedLocalidad}
                        getOptionLabel={(o) => o?.nombre || ''}
                        isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                        disableClearable={required}
                        loading={loadingLocalidades}
                        onChange={(_, opt) => handleLocalidadChange(opt)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Localidad"
                                required={required}
                                sx={mobileInputSx}
                            />
                        )}
                        {...autocompletePopperSx}
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 8 }}>
                    <Autocomplete
                        size="small"
                        disabled={disabled || !state.geo_localidad_id}
                        options={calleOptions}
                        value={calleValue}
                        getOptionLabel={(o) => (typeof o === 'string' ? o : o?.nombre || '')}
                        isOptionEqualToValue={(a, b) => {
                            if (a?.sinCalle || b?.sinCalle) return Boolean(a?.sinCalle) && Boolean(b?.sinCalle);
                            return Number(a?.id) === Number(b?.id);
                        }}
                        freeSolo
                        filterOptions={(opts) => opts}
                        inputValue={calleInput}
                        onInputChange={(_, v, reason) => {
                            if (reason === 'reset') return;
                            setCalleInput(v);
                            if (v.trim().toLowerCase() === 'sin calle') {
                                applySinCalle();
                                return;
                            }
                            emit({ calle_texto: v, geo_calle_id: null });
                        }}
                        onChange={(_, opt) => {
                            if (opt && typeof opt === 'object' && opt.sinCalle) {
                                applySinCalle();
                                return;
                            }
                            if (opt && typeof opt === 'object') {
                                setCalleInput(opt.nombre);
                                emit({ geo_calle_id: opt.id, calle_texto: opt.nombre });
                                return;
                            }
                            if (typeof opt === 'string') {
                                if (opt.trim().toLowerCase() === 'sin calle') {
                                    applySinCalle();
                                    return;
                                }
                                setCalleInput(opt);
                                emit({ calle_texto: opt, geo_calle_id: null });
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Calle"
                                required={required && !sinCalle}
                                sx={mobileInputSx}
                            />
                        )}
                        {...autocompletePopperSx}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                        size="small"
                        fullWidth
                        label="Altura"
                        disabled={disabled || !state.geo_localidad_id || sinCalle}
                        required={required && !sinCalle}
                        value={sinCalle ? '' : (state.altura || '')}
                        onChange={(e) => emit({ altura: e.target.value })}
                        sx={mobileInputSx}
                        helperText={sinCalle ? 'No aplica con Sin calle.' : undefined}
                    />
                </Grid>

                {Boolean(state.geo_localidad_id) && (
                    <Grid size={12}>
                        <OsmLocationMap
                            lat={state.latitud}
                            lng={state.longitud}
                            zoom={mapZoom}
                            disabled={disabled}
                            loading={geocoding}
                            onChange={({ lat, lng, origen_coords }) => {
                                setGeoError('');
                                emit({
                                    latitud: lat,
                                    longitud: lng,
                                    origen_coords,
                                });
                            }}
                        />
                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                            {locationStatus === 'ok' && (
                                <Chip size="small" color="success" label="Ubicación determinada automáticamente." />
                            )}
                            {locationStatus === 'manual_required' && (
                                <Chip
                                    size="small"
                                    color="warning"
                                    label="No se pudo ubicar automáticamente. Corresponde marcar el punto en el mapa."
                                />
                            )}
                            {locationStatus === 'manual_ok' && (
                                <Chip size="small" color="info" label="Ubicación confirmada en el mapa." />
                            )}
                        </Box>
                        {sinCalle && locationStatus === 'manual_required' && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                Sin calle: corresponde marcar la ubicación exacta en el mapa.
                            </Alert>
                        )}
                        <FormHelperText sx={{ mx: 0, mt: 1 }}>
                            {geocoding
                                ? 'Se está ubicando la dirección…'
                                : 'El pin sigue la dirección; puede ajustarse con clic o arrastre.'}
                        </FormHelperText>
                        <FormHelperText sx={{ mx: 0 }}>{coordsLabel}</FormHelperText>
                        {geoError && (
                            <FormHelperText error sx={{ mx: 0 }}>{geoError}</FormHelperText>
                        )}
                        {required && missingCoords && (
                            <FormHelperText error sx={{ mx: 0 }}>
                                La localidad no tiene coordenadas de centroide en el catálogo.
                            </FormHelperText>
                        )}
                    </Grid>
                )}

                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                        size="small"
                        fullWidth
                        label="Piso"
                        disabled={disabled}
                        value={state.piso || ''}
                        onChange={(e) => emit({ piso: e.target.value })}
                        sx={mobileInputSx}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                        size="small"
                        fullWidth
                        label="Depto"
                        disabled={disabled}
                        value={state.depto || ''}
                        onChange={(e) => emit({ depto: e.target.value })}
                        sx={mobileInputSx}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                        size="small"
                        fullWidth
                        label="Referencia"
                        disabled={disabled}
                        value={state.referencia || ''}
                        onChange={(e) => emit({ referencia: e.target.value })}
                        sx={mobileInputSx}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}

export function geoDireccionToApi(value) {
    if (!value || !value.geo_localidad_id) return null;
    if (value.latitud == null || value.longitud == null || !value.origen_coords) return null;
    if (value.origen_coords === 'centroide') return null;

    const sin = isSinCalle(value);
    if (sin && value.origen_coords !== 'mapa') return null;

    return {
        geo_localidad_id: Number(value.geo_localidad_id),
        geo_calle_id: value.geo_calle_id ? Number(value.geo_calle_id) : null,
        calle_texto: sin ? SIN_CALLE_LABEL : (value.calle_texto || ''),
        altura: sin ? '' : (value.altura || ''),
        piso: value.piso || '',
        depto: value.depto || '',
        referencia: value.referencia || '',
        codigo_postal: value.codigo_postal || '',
        latitud: Number(value.latitud),
        longitud: Number(value.longitud),
        origen_coords: value.origen_coords,
    };
}

export function geoDireccionFromApi(api) {
    if (!api) return null;
    return {
        geo_provincia_id: api.geo_provincia_id || '',
        geo_departamento_id: api.geo_departamento_id || '',
        geo_localidad_id: api.geo_localidad_id || '',
        geo_calle_id: api.geo_calle_id || null,
        calle_texto: api.calle_texto || '',
        altura: api.altura || '',
        piso: api.piso || '',
        depto: api.depto || '',
        referencia: api.referencia || '',
        codigo_postal: api.codigo_postal || '',
        latitud: api.latitud,
        longitud: api.longitud,
        origen_coords: api.origen_coords,
        provincia_nombre: api.provincia_nombre || '',
        departamento_nombre: api.departamento_nombre || '',
        localidad_nombre: api.localidad_nombre || '',
    };
}

export { emptyValue as emptyGeoDireccion };
